import { $lang, getTag, ModalRef, useApiClient, useAsyncCache, useTranslation } from '@msdk/common'
import {
  DateTimeFieldAccuracy,
  dateTimeToHourMinuteString,
  DateTimeValue,
  Entity,
  getElapsedDaysFromDate,
} from '@msdk/definitions'
import { stringFormat } from '@msdk/forms'
import {
  Box,
  ButtonIcon,
  ButtonPrimary,
  ButtonSecondary,
  CheckboxMultiple,
  CloseIcon,
  COLORS,
  DialogActions,
  DialogTitle,
  Divider,
  FileDownloadOutlinedIcon,
  InfoIcon,
  Spinner,
  styled,
  Typography,
} from '@msdk/ui'
import { useEffect, useState } from 'react'
import { useRecoilValue } from 'recoil'

const StyledDialogTitle = styled(DialogTitle)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${COLORS.grey50};
`

const Content = styled(Box)`
  padding: 16px 24px;
  height: 444px;
  width: 600px;
  overflow-y: auto;
`

const StyledBox = styled(Box)`
  background-color: ${COLORS.info.light};
  border: 1px solid ${COLORS.info.outlinedBorder};
  border-radius: 8px;
  height: 50px;
  gap: 8px;
  display: flex;
  align-items: center;
  padding: 0 8px;
`

const StyledSpinnerContainer = styled(Box)`
  width: 100%;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const StyledDiv = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`

export function ManualsDownload({ modalRef }: { modalRef?: ModalRef }) {
  // Hooks
  const client = useApiClient()
  const cache = useAsyncCache()
  const t = useTranslation()
  const userLanguage = useRecoilValue($lang)

  // State variables
  const [documents, setDocuments] = useState<Entity[]>([])
  const [selectedManuals, setSelectedManuals] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)

  /**
   * Creates a list of files to download and iterates through it to donwload each item.
   */
  const handleDownload = async () => {
    const toDownload = documents
      .filter((e) => selectedManuals.includes(e.id))
      .map((v) => ({
        name: v.fields['NAME']?.value,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        originalName: (v.fields['FILE'] as any)?.originalName as string,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        url: (v.fields['FILE'] as any)?.url as string,
        entityId: v.id as string,
        atId: v.fields['FILE']?.value,
      }))

    for (let i = 0; i < toDownload.length; i++) {
      client.downloadFile(
        `CMPS/api/AdminPortal/documentfile/DOCUMENT/${toDownload[i].entityId}/FILE/${toDownload[i].atId}`,
        toDownload[i].originalName,
      )
    }
  }

  /**
   * Used to get the elapsed time since the start date.
   * @param startDate - The start date in ISO format.
   * @returns The elapsed time label.
   */
  const getETA = (startDate: string) => {
    const days = getElapsedDaysFromDate(startDate)

    if (days === 0) {
      const todayString = t('CMPSR_UPLOADED_TODAY_HOUR', 'Uploaded Today, {0}')
      const dateTimeValue = new DateTimeValue(startDate)
      const timeString = dateTimeValue ? dateTimeToHourMinuteString(dateTimeValue, userLanguage) : ''
      return stringFormat(todayString, timeString)
    }

    const elapsedLabel = t('CMPSR_UPLOADED_DATE', 'Uploaded {0}')
    const date = new DateTimeValue(startDate, DateTimeFieldAccuracy.DateTime).toStringForView()
    return stringFormat(elapsedLabel, `${date}`)
  }

  const closeModal = () => modalRef && modalRef.close()

  useEffect(() => {
    /**
     * Getting the parent element and resetting padding
     */
    const content = document.querySelector('.MuiDialogContent-root') as HTMLElement
    content.style.padding = '0'

    const fetchDocuments = async () => {
      setIsLoading(true)

      const res = await cache.getOrCreate('user-manuals', () => client.read(`CMPS/api/AdminPortal/GetManuals`))
      setDocuments(res as Entity[])

      setIsLoading(false)
    }

    fetchDocuments()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <StyledDialogTitle>
        <Typography variant="h6">{t('CMPSR_USR_MAN', 'User Manuals')}</Typography>
        <ButtonIcon onClick={closeModal} data-test-id={getTag('SHELL', 'USER_MANUALS', 'CLOSE_ICON', 'button')}>
          <CloseIcon />
        </ButtonIcon>
      </StyledDialogTitle>
      <Divider />
      <Content>
        <StyledBox>
          <InfoIcon color="info" />
          <Typography variant="body2" color={COLORS.info.main}>
            {t('CMPSR_USR_MAN_INFO', 'At least one User Manual should be selected')}
          </Typography>
        </StyledBox>
        {isLoading && (
          <StyledSpinnerContainer>
            <Spinner />
          </StyledSpinnerContainer>
        )}
        {!isLoading && (
          <CheckboxMultiple
            allLabel={t('CMPSR_USR_MAN_CHECK_ALL', 'All')}
            disabled={!documents.length}
            onChange={(values) => setSelectedManuals(values)}
            options={documents
              .slice()
              .sort((a, b) => {
                const dateA = a.fields['CREATION_DATE']?.value || ''
                const dateB = b.fields['CREATION_DATE']?.value || ''
                return new Date(dateB).getTime() - new Date(dateA).getTime()
              })
              .map((v) => ({
                value: v.id,
                title: v.fields['NAME']?.value,
                subtitle: getETA(v.fields['CREATION_DATE']?.value),
              }))}
            values={selectedManuals}
          />
        )}
      </Content>
      <Divider />
      <DialogActions>
        <ButtonSecondary onClick={closeModal} data-test-id={getTag('SHELL', 'USER_MANUALS', 'CANCEL', 'button')}>
          <Typography variant="button">{t('CMPSR_USR_MAN_CANCEL', 'Cancel')}</Typography>
        </ButtonSecondary>
        <ButtonPrimary
          disabled={!selectedManuals.length}
          onClick={handleDownload}
          data-test-id={getTag('SHELL', 'USER_MANUALS', 'DOWNLOAD', 'button')}
        >
          <StyledDiv>
            <FileDownloadOutlinedIcon />
            <Typography variant="button">{t('CMPSR_USR_MAN_DOWNLOAD', 'Download')}</Typography>
          </StyledDiv>
        </ButtonPrimary>
      </DialogActions>
    </>
  )
}
